import { hexToHSL } from "@/lib/color-utils"

export function getAccessibleTextColor(backgroundColor: string) {
  // Convert HSL to RGB
  const h = Number.parseInt(backgroundColor.split(" ")[0] ?? "0")
  const s = Number.parseInt(backgroundColor.split(" ")[1] ?? "0")
  const l = Number.parseInt(backgroundColor.split(" ")[2] ?? "0")

  // HSL to RGB conversion
  const c = (1 - Math.abs(2 * l / 100 - 1)) * s / 100
  const x = c * (1 - Math.abs((h / 60) % 2 - 1))
  const m = l / 100 - c / 2

  let r: number
  let g: number
  let b: number
  if (h < 60) {
    [r, g, b] = [c, x, 0]
  }
  else if (h < 120) {
    [r, g, b] = [x, c, 0]
  }
  else if (h < 180) {
    [r, g, b] = [0, c, x]
  }
  else if (h < 240) {
    [r, g, b] = [0, x, c]
  }
  else if (h < 300) {
    [r, g, b] = [x, 0, c]
  }
  else {
    [r, g, b] = [c, 0, x]
  }

  const [rr, gg, bb] = [(r + m) * 255, (g + m) * 255, (b + m) * 255].map(Math.round)

  // Calculate luminance of background
  const bgLuminance = calculateRelativeLuminance(rr ?? 0, gg ?? 0, bb ?? 0)

  // Calculate contrast with white and black
  const whiteContrast = calculateContrastRatio(1, bgLuminance)
  const blackContrast = calculateContrastRatio(0, bgLuminance)

  // Return the color with better contrast (minimum 4.5:1 for WCAG AA)
  return whiteContrast >= blackContrast ? "0 0% 100%" : "0 0% 0%"
}

function calculateRelativeLuminance(r: number, g: number, b: number) {
  const [rs, gs, bs] = [r, g, b].map((channel) => {
    const c = channel / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * (rs ?? 0) + 0.7152 * (gs ?? 0) + 0.0722 * (bs ?? 0)
}

function calculateContrastRatio(l1: number, l2: number) {
  const lighter = Math.max(l1, l2)
  const darker = Math.min(l1, l2)
  return (lighter + 0.05) / (darker + 0.05)
}

export function calculateColor(color: string | undefined) {
  if (!color)
    return "0 0% 100%" // white
  const hslColor = hexToHSL(color)
  return getAccessibleTextColor(`${hslColor.h} ${hslColor.s} ${hslColor.l}`)
}
/**
 * Sanitize a string to be used as a CSS class name.
 * It replaces all non-alphanumeric characters with a dash.
 * @param className - The class name to sanitize
 * @returns The sanitized class name.
 */
export function sanitizeCssClassName(className: string) {
  return className.replace(/[^a-z0-9]/gi, "-")
}
